import { Trans, useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'
import clsx from 'clsx'

import { AlignCenterArrow16, Setting24 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant, DD, Switch, SwitchSize } from '@makyoapp/makyo-ui'

import { useGridV2Context } from '@/components/GridV2/contexts/GridV2Context'

import { useGridLayoutOptions } from '../../hooks/useGridLayoutOptions'
import { AppendData } from './AppendData'
import { ClearRows } from './ClearRows'
import { ColumnVisibility } from './ColumnVisibility'
import { DeleteTable } from './DeleteTable'
import { DuplicateTable } from './DuplicateTable'
import { ExportData } from './ExportData'
import { RenameTable } from './RenameTable'
import { TimezoneSelect } from './TimezoneSelect'

interface TableSettingCheckBoxProps {
  label: string
  value: boolean
  setValue: (nextValue: boolean) => void
}

function TableSettingCheckBox({ label, value, setValue }: TableSettingCheckBoxProps) {
  const { t } = useTranslation()

  return (
    <label className='flex-c4 mk-dd-row cursor-pointer'>
      <div className='w-full'>{t(label)}</div>
      <Switch
        checked={value}
        size={SwitchSize.XS}
        onClick={() => {
          setValue(!value)
        }}
      />
    </label>
  )
}

/**
 * @todo:
 *  - import data
 *  - rename table
 *  - assign as user table
 */
export function LayoutSettingsMenu() {
  const { table, handleResetColumnsWidth } = useGridV2Context()
  const [layoutOptions, setLayoutOptions] = useGridLayoutOptions()

  const [show, setShow] = useBoolean(false)

  const handleToggleMenu = () => {
    const nextShow = !show
    setShow(nextShow)
  }

  const canClearRows = !table?.isSnapshot && !table?.isQuery
  const canAppend = !table?.isSnapshot && !table?.isQuery
  const canDelete = !table?.isSessionTable && !table?.isUserTable
  const canDuplicate = !!table?.isQuery

  if (!table) return null

  return (
    <DD
      id='mk-ag-tbl-setting'
      toggleNoButton
      menuClassName='mk-dd-menu border bdr py-2 w-[240px]'
      menuNoPad
      menuDefaultSize={false}
      withNotch
      notchClassName={clsx(
        'bottom-[calc(100%+2px)]! mk-notch-pos',
        'bg-white dark:bg-slate-900 border-t border-l bdr mt-[-1px]'
      )}
      placement={'bottom-start'}
      show={show}
      onToggle={handleToggleMenu}
      toggle={(_, toggleProps) => {
        return (
          <Button
            variant={ButtonVariant.Ghost}
            noPadding
            className='w-9'
            {...toggleProps}
            icon={<Setting24 width={16} height={16} />}
          />
        )
      }}
    >
      <span className='dd-menu-title text-light block'>
        <Trans>Time Zone</Trans>
      </span>

      <TimezoneSelect />

      <span className='mt-2 dd-menu-title text-light block'>
        <Trans>Table Settings</Trans>
      </span>

      <ColumnVisibility />

      {canAppend && <AppendData />}

      <ExportData />

      <button className='mk-ddi flex-c mk-dd-row' onClick={handleResetColumnsWidth}>
        <AlignCenterArrow16 />
        <Trans>Size Columns to Fit</Trans>
      </button>

      {canDuplicate && <DuplicateTable />}

      <RenameTable />

      {canClearRows && <ClearRows />}

      {canDelete && <DeleteTable />}

      <hr className='hr' />

      <TableSettingCheckBox
        label='Language Selection'
        value={layoutOptions.showLang}
        setValue={(val) => {
          setLayoutOptions((prev) => ({
            ...prev,
            showLang: val,
          }))
        }}
      />

      <TableSettingCheckBox
        label='User Selection'
        value={layoutOptions.showUser}
        setValue={(val) => {
          setLayoutOptions((prev) => ({
            ...prev,
            showUser: val,
          }))
        }}
      />

      <TableSettingCheckBox
        label='Raw Data'
        value={layoutOptions.showRaw}
        setValue={(val) => {
          setLayoutOptions((prev) => ({
            ...prev,
            showRaw: val,
          }))
        }}
      />
    </DD>
  )
}
